import { FC, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Markdown from "react-markdown";
import dayjs from "dayjs";
import { IActionItemSubject } from "api/actionItems";

// RM-3943: what a request asks for, shared by the To-do page and the emailed
// no-login link page.

export const NOTE_MAX_LENGTH = 2000;

interface IRequest {
  title: string;
  instructions: string | null;
  action_url: string | null;
  who_must_act: string | null;
  subject: IActionItemSubject | null;
  due_at: string | null;
  rejection_note: string | null;
  overdue?: boolean;
}

// "Open appstoreconnect.apple.com" reads better than a bare "Open link". Only
// http(s) links get a button, so a bad URL can't become a javascript: link.
export const linkLabel = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol)
      ? `Open ${parsed.hostname}`
      : null;
  } catch {
    return null;
  }
};

export const dueLabel = (
  dueAt: string | null,
  overdue = false
): string | null => {
  if (!dueAt) return null;
  const date = dayjs(dueAt).format("MMM D, YYYY");
  return overdue ? `Overdue (due ${date})` : `Due ${date}`;
};

// Instructions are Markdown written by Riivet staff; react-markdown renders no
// raw HTML. Links open in a new tab so the request stays on screen.
const MarkdownLink = ({ node, children, ...props }: any) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

export const ActionItemDetails: FC<{ item: IRequest }> = ({ item }) => {
  const due = dueLabel(item.due_at, item.overdue);
  const subject = item.subject?.type === "Account" ? null : item.subject?.label;
  const link = item.action_url ? linkLabel(item.action_url) : null;
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="h6" component="h3">
          {item.title}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ mt: 0.5, flexWrap: "wrap" }}
        >
          {subject && <Chip size="small" label={subject} />}
          {due && (
            <Chip
              size="small"
              color={item.overdue ? "error" : "default"}
              label={due}
            />
          )}
        </Stack>
      </Box>
      {item.rejection_note && (
        <Alert severity="warning">
          <strong>Riivet checked this and it isn't done yet:</strong>{" "}
          {item.rejection_note}
        </Alert>
      )}
      {item.who_must_act && (
        <Typography variant="body2">
          <strong>Who must do this:</strong> {item.who_must_act}
        </Typography>
      )}
      {item.instructions && (
        <Box
          className="action-item-instructions"
          sx={{ "& p:last-child": { mb: 0 } }}
        >
          <Markdown components={{ a: MarkdownLink }}>
            {item.instructions}
          </Markdown>
        </Box>
      )}
      {item.action_url && link && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            href={item.action_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

// "I've done this" with an optional note for Riivet. The note is multiline,
// so it is sent as typed (owl strips it).
export const DoneForm: FC<{ onDone: (note?: string) => Promise<void> }> = ({
  onDone,
}) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const tooLong = note.length > NOTE_MAX_LENGTH;

  const submit = async () => {
    if (tooLong) return;
    setSaving(true);
    try {
      await onDone(note.trim() ? note : undefined);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1}>
      <TextField
        label="Note for Riivet (optional)"
        placeholder="e.g. Invited play@riivet.com as Release Manager"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        multiline
        minRows={2}
        size="small"
        fullWidth
        error={tooLong}
        helperText={
          tooLong
            ? `Keep the note under ${NOTE_MAX_LENGTH} characters`
            : undefined
        }
      />
      <Box>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving || tooLong}
        >
          {saving ? "Sending…" : "I've done this"}
        </Button>
      </Box>
    </Stack>
  );
};
